import React, { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

import { useTranslation } from 'react-i18next';

export function langFromPath(filePath: string): string {
  const ext = filePath.split('.').pop()?.toLowerCase() ?? '';
  const map: Record<string, string> = {
    py: 'python',
    js: 'javascript',
    ts: 'typescript',
    tsx: 'tsx',
    jsx: 'jsx',
    sh: 'bash',
    bash: 'bash',
    zsh: 'bash',
    tf: 'hcl',
    tfvars: 'hcl',
    json: 'json',
    yaml: 'yaml',
    yml: 'yaml',
    md: 'markdown',
    html: 'html',
    htm: 'html',
    css: 'css',
    scss: 'scss',
    xml: 'xml',
    go: 'go',
    rs: 'rust',
    java: 'java',
    rb: 'ruby',
    php: 'php',
    sql: 'sql',
    toml: 'toml',
    ini: 'ini',
    env: 'bash',
    graphql: 'graphql',
    swift: 'swift',
    kt: 'kotlin',
    cs: 'csharp',
    c: 'c',
    cpp: 'cpp',
    h: 'c',
  };
  return map[ext] ?? 'text';
}

export function langColor(lang: string): string {
  const colors: Record<string, string> = {
    python: '#3b82f6',
    javascript: '#eab308',
    typescript: '#60a5fa',
    tsx: '#60a5fa',
    jsx: '#eab308',
    bash: '#22c55e',
    hcl: '#a855f7',
    json: '#f97316',
    yaml: '#f87171',
    markdown: '#9ca3af',
    html: '#f97316',
    css: '#93c5fd',
    go: '#22d3ee',
    rust: '#ea580c',
    java: '#ef4444',
    sql: '#ec4899',
    csharp: '#6366f1',
    graphql: '#e535ab',
  };
  return colors[lang] ?? '#6b7280';
}

export function CopyButton({
  text,
  className = '',
}: {
  text: string;
  className?: string;
}) {
  const { t } = useTranslation('pleri/chat');
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button
      onClick={copy}
      className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-all duration-150 ${
        copied
          ? 'bg-emerald-500/20 text-emerald-400'
          : 'text-gray-500 hover:bg-gray-700/60 hover:text-gray-300'
      } ${className}`}
      title={copied ? t('copiedExclamation') : t('copyToClipboard')}
    >
      {copied ? (
        <>
          <svg
            className="h-3 w-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4.5 12.75l6 6 9-13.5"
            />
          </svg>
          {t('copied')}
        </>
      ) : (
        <>
          <svg
            className="h-3 w-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          {t('copy')}
        </>
      )}
    </button>
  );
}

export function Spinner({
  className = 'text-yellow-400',
}: {
  className?: string;
}) {
  return (
    <svg
      className={`h-3 w-3 animate-spin ${className}`}
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

export function TrafficLights() {
  return (
    <div className="flex gap-1.5">
      <div className="h-2.5 w-2.5 rounded-full bg-gray-700" />
      <div className="h-2.5 w-2.5 rounded-full bg-gray-700" />
      <div className="h-2.5 w-2.5 rounded-full bg-gray-700" />
    </div>
  );
}

export function CollapseButton({
  collapsed,
  onClick,
}: {
  collapsed: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation('pleri/chat');
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-gray-600 transition-colors hover:bg-gray-700/60 hover:text-gray-400"
      title={collapsed ? t('expand') : t('collapse')}
    >
      <svg
        className={`h-3 w-3 transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4.5 15.75l7.5-7.5 7.5 7.5"
        />
      </svg>
    </button>
  );
}

interface CodePaneProps {
  code: string;
  lang: string;
  startLine?: number;
  maxHeight?: string;
  /** When 'fill', renders without a scroll wrapper (parent controls scroll/height) */
}

export function CodePane({
  code,
  lang,
  startLine = 1,
  maxHeight = '18rem',
}: CodePaneProps) {
  const highlighter = (
    <SyntaxHighlighter
      language={lang}
      style={vscDarkPlus}
      customStyle={{
        margin: 0,
        padding: '12px 0',
        background: 'transparent',
        fontSize: '12px',
        lineHeight: '1.65',
      }}
      codeTagProps={{
        style: {
          fontFamily: 'ui-monospace, "Cascadia Code", "Fira Code", monospace',
        },
      }}
      showLineNumbers
      startingLineNumber={startLine}
      lineNumberStyle={{
        minWidth: '3em',
        paddingRight: '1.25em',
        paddingLeft: '0.75em',
        color: '#374151',
        fontSize: '12px',
        userSelect: 'none',
        borderRight: '1px solid #1f2937',
        marginRight: '1em',
      }}
      wrapLongLines={false}
    >
      {code}
    </SyntaxHighlighter>
  );

  if (maxHeight === 'none') {
    return highlighter;
  }
  return (
    <div className="overflow-auto" style={{ maxHeight }}>
      {highlighter}
    </div>
  );
}
